import { OfferRequest } from '@btcdirect/stores';

/** Outgoing offer parameters after they've been converted to their correct types */
export type OfferParameters = OfferRequest & {
    quoteCurrency?: string;
    quoteCurrencyAmount?: number;
    baseCurrency?: string;
    baseCurrencyAmount?: number;
    paymentMethod?: string;
    walletAddress?: string;
    walletAddressTag?: string;
};

/** Incoming offer parameters where each value is a string */
type BaseOfferParameters = { [Property in keyof OfferParameters]?: string };

const ParameterTypes = {
    quoteCurrency: 'string',
    quoteCurrencyAmount: 'number',
    baseCurrency: 'string',
    baseCurrencyAmount: 'number',
    paymentMethod: 'string',
    walletAddress: 'string',
    walletAddressTag: 'string',
    signature: 'string',
    returnUrl: 'string',
    callbackUrl: 'string',
    partnerOrderIdentifier: 'string',
    expiryDateTime: 'string',
    fixedAmount: 'boolean',
    fixedCurrency: 'boolean',
    fixedPaymentMethod: 'boolean',
    showWalletAddress: 'boolean'
};

/**
 * Get the available query params from the url
 * This only returns the expected query params and filters out the rest
 *
 * @param params
 */
export function getOfferParameters(parameters: BaseOfferParameters | OfferParameters): OfferParameters {
    if (!parameters) {
        return {};
    }

    const filtered = filterUnexpectedParameters(parameters);

    const converted = convertNonStringValues(filtered);

    if (converted.quoteCurrency) {
        converted.quoteCurrency = (<string>converted.quoteCurrency).toUpperCase();
    }

    if (converted.baseCurrency) {
        converted.baseCurrency = (<string>converted.baseCurrency).toUpperCase();
    }

    // If both fiat amount and crypto amount are provided, remove crypto amount
    if (converted.quoteCurrencyAmount && converted.baseCurrencyAmount) {
        converted.baseCurrencyAmount = null;
    }

    return converted;
}

/**
 * Filter out any unexpected parameters.
 * This actually deletes the unexpected parameters from the URLSearchParams object.
 *
 * @param parameters
 */
function filterUnexpectedParameters(parameters: BaseOfferParameters | OfferParameters): BaseOfferParameters | OfferParameters {
    const allowedKeys = Object.keys(ParameterTypes);

    const keys = Object.keys(parameters);
    for (const key of keys) {
        if (!allowedKeys.includes(key)) {
            delete parameters[key];
        }
    }

    return parameters;
}

/**
 * Convert non-string values to their correct type
 *
 * @param parameters
 */
function convertNonStringValues(parameters: BaseOfferParameters | OfferParameters): OfferParameters {
    const result: OfferParameters = {};

    const entries = Object.entries(parameters);
    for (const [key, value] of entries) {
        const type = ParameterTypes[key];

        // If the value is already the correct type, use it
        if (typeof value === type) {
            result[key] = value;
            continue;
        }

        // If the value is empty, null or undefined, convert it to null
        if ((![false, ''].includes(value) && !value) || value === 'null' || value === 'undefined') {
            result[key] = null;
            continue;
        }

        // Convert the value to the correct type
        try {
            switch (type) {
                case 'string':
                    result[key] = String(value);
                    break;
                case 'number':
                    result[key] = Number(value);
                    break;
                case 'boolean':
                    result[key] = value === 'true' || value === '1';
                    break;
                default:
                    result[key] = value;
                    break;
            }
        } catch (error) {
            console.error(`Incorrect value for ${key} in 'set-parameter'`, {
                value,
                type
            });
        }
    }

    return result;
}
